import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  CircularProgress,
  Pagination,
  PaginationItem, 
} from '@mui/material';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
// Debounce hook
function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const AuditHistory = () => {
  const [selectedActivity, setSelectedActivity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [auditData, setAuditData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm);

  const fetchAuditData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/v1/get-activity-list?page=${page}&activity_type=${selectedActivity}`, {
        withCredentials: true,
      });

      const result = res.data;
      const data = result.data || [];

      setOriginalData(data);
      setTotalPages(result?.meta?.totalPages || 1);

      if (Array.isArray(result.uniqueTypes)) {
        setActivityTypes(result.uniqueTypes);
      }
    } catch (error) {
      console.error('Error fetching audit data:', error);
    } finally {
      setLoading(false);
    }
  }, [page, selectedActivity]);

  useEffect(() => {
    fetchAuditData();
  }, [fetchAuditData]);

  const filteredData = useMemo(() => {
    return originalData.filter((item) => {
      const matchesActivity = !selectedActivity || item.activity_type === selectedActivity;
      const matchesSearch = item.activity_description?.toLowerCase().includes(debouncedSearchTerm);
      return matchesActivity && matchesSearch;
    });
  }, [originalData, selectedActivity, debouncedSearchTerm]);

  useEffect(() => {
    setAuditData(filteredData);
  }, [filteredData]);

  const handlePageChange = (event, value) => setPage(value);

  const handleFilterChange = (e) => {
    setSelectedActivity(e.target.value);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
    setPage(1);
  };

  return (
    <Box sx={{ p: 3, borderRadius: 3, border: 1, borderColor: 'divider', boxShadow: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" alignItems="center" spacing={1}>   
          <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 0.7, borderRadius: 50, fontSize: 5 }}><ManageHistoryIcon></ManageHistoryIcon></Box>
          <Typography variant="h6" fontWeight="bold" color='#544040'>Audit History</Typography>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField size="small" placeholder="Search descriptions..." onChange={handleSearchChange} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by Activity Type</InputLabel>
            <Select value={selectedActivity} onChange={handleFilterChange} label="Filter by Activity Type">
              <MenuItem value="">All</MenuItem>
              {activityTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Activity Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Updated By</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  auditData.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell>{item.activity_type?.replace(/_/g, ' ') || '—'}</TableCell>
                      <TableCell>{item.activity_description || '—'}</TableCell>
                      <TableCell>{item.user?.username || '—'}</TableCell>
                      <TableCell>{item.ip_address || '—'}</TableCell>
                      <TableCell>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            fontWeight: 'bold',
                            fontSize: 12,
                            textTransform: 'capitalize',
                            color: 'white',
                            bgcolor: (theme) => {
                              switch ((item.status || '').toLowerCase()) {
                                case 'success':
                                  return theme.palette.success.main;
                                case 'pending':
                                  return theme.palette.warning.main;
                                case 'failed':
                                  return theme.palette.error.main;
                                default:
                                  return theme.palette.grey[500];
                              }
                            },
                          }}
                        >
                          {item.status || '—'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              renderItem={(item) => <PaginationItem {...item} />}
              color="primary"
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default AuditHistory;
